import { extendTheme as extendJoyTheme } from "@mui/joy/styles";
import { grey, red } from "@mui/material/colors";
import {
  experimental_extendTheme as extendMuiTheme,
  responsiveFontSizes,
} from "@mui/material/styles";
import { deepmerge } from "@mui/utils";

export const getMuiTheme = (alpha: boolean) => {
  const brand = {
    50: "#FCEEE8",
    100: "#F7D6C8",
    200: "#F1BEA8",
    300: "#EBA688",
    400: "#DF8660",
    500: "#D3663E",
    600: "#C65D38",
    700: "#B15130",
    800: "#9C4529",
    900: "#863A22",
  };
  const muiTheme = extendMuiTheme({
    colorSchemes: {
      light: {
        palette: {
          primary: alpha
            ? { ...red }
            : {
                main: brand[500],
                light: brand[300],
                dark: brand[700],
                contrastText: "#fff",
              },
        },
      },
      dark: {
        palette: {
          primary: alpha
            ? { ...red }
            : {
                main: brand[500],
                light: brand[300],
                dark: brand[700],
                contrastText: "#fff",
              },
        },
      },
    },
    typography: {
      h4: {
        fontWeight: 600,
      },
      button: {
        textTransform: "none",
        fontSize: 14,
      },
      fontFamily:
        'asap, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    },
    components: {
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
          },
        },
      },
    },
  });

  return responsiveFontSizes(muiTheme);
};

export const getJoyTheme = (alpha: boolean) => {
  const muiTheme = getMuiTheme(alpha);
  const brand = {
    50: "#FCEEE8",
    100: "#F7D6C8",
    200: "#F1BEA8",
    300: "#EBA688",
    400: "#DF8660",
    500: "#D3663E",
    600: "#C65D38",
    700: "#B15130",
    800: "#9C4529",
    900: "#863A22",
  };

  return extendJoyTheme({
    // This is required to point to `var(--mui-*)` because we are using `CssVarsProvider` from Material UI.
    cssVarPrefix: "mui",
    colorSchemes: {
      light: {
        palette: {
          primary: {
            ...brand,
            main: brand[500],
            light: brand[300],
            dark: brand[700],
            contrastText: "#fff",
          },
          neutral: {
            ...grey,
          },
          // Do the same for the `danger`, `info`, `success`, and `warning` palettes,
          divider: "var(--mui-palette-divider)",
          text: {
            tertiary: "rgba(0 0 0 / 0.56)",
          },
        },
      },
      dark: {
        palette: {
          primary: {
            ...brand,
            main: brand[500],
            light: brand[300],
            dark: brand[700],
            contrastText: "#fff",
          },
          neutral: {
            ...grey,
          },
          // Do the same for the `danger`, `info`, `success`, and `warning` palettes,
          divider: "var(--mui-palette-divider)",
          text: {
            tertiary: "rgba(0 0 0 / 0.56)",
          },
        },
      },
      // Do the same for dark mode
      // dark: { ... }
    },
    fontFamily: {
      display:
        'asap, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
      body: 'asap, sans-serif, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol"',
    },
    shadow: {
      xs: "none",
      sm: "none",
      md: "none",
      lg: "none",
      xl: "none",
    },
    components: {
      JoyInput: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            "--Input-focusedHighlight": "transparent",
          },
        },
      },
      JoySelect: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            "--Select-focusedHighlight": "transparent",
          },
        },
      },
      JoyAutocomplete: {
        styleOverrides: {
          root: {
            boxShadow: "none",
            "--Input-focusedHighlight": "transparent",
          },
        },
      },
    },
  });
};

export const getTheme = (alpha: boolean) => {
  const muiTheme = getMuiTheme(alpha);
  const joyTheme = getJoyTheme(alpha);
  // Joy tokens (e.g. `theme.shadow.sm`) must take precedence for Joy's sx parser.
  const theme = deepmerge(muiTheme, joyTheme) as {
    palette?: {
      primary?: {
        main?: string;
        light?: string;
        dark?: string;
        contrastText?: string;
      };
    };
  };

  const fallbackPrimary =
    muiTheme?.palette?.primary ??
    muiTheme?.colorSchemes?.light?.palette?.primary;
  if (!theme.palette) theme.palette = {};
  if (!theme.palette.primary) theme.palette.primary = {};
  theme.palette.primary.main =
    theme.palette.primary.main ?? fallbackPrimary?.main ?? "#D3663E";
  theme.palette.primary.light =
    theme.palette.primary.light ?? fallbackPrimary?.light ?? "#EBA688";
  theme.palette.primary.dark =
    theme.palette.primary.dark ?? fallbackPrimary?.dark ?? "#B15130";
  theme.palette.primary.contrastText =
    theme.palette.primary.contrastText ??
    fallbackPrimary?.contrastText ??
    "#fff";

  return theme;
};
